import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'pl', label: 'Polski', short: 'PL' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'fr', label: 'Français', short: 'FR' },
];

export default function LanguageSwitcher({ light = false }: { light?: boolean }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
          light
            ? 'text-stone-700 hover:bg-stone-100'
            : 'text-white hover:bg-white/15'
        }`}
        aria-label={t('lang.label')}
      >
        <span className="flex h-5 w-5 items-center justify-center text-base">
          <i className="ri-global-line"></i>
        </span>
        <span>{current.short}</span>
        <span className="flex h-4 w-4 items-center justify-center text-sm">
          <i className="ri-arrow-down-s-line"></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-stone-200 bg-white">
          <div className="py-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2.5 text-left text-sm transition-colors cursor-pointer ${
                  i18n.language === lang.code
                    ? 'bg-emerald-50 font-semibold text-emerald-700'
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                <span>{lang.label}</span>
                {i18n.language === lang.code && (
                  <span className="flex h-4 w-4 items-center justify-center text-sm text-emerald-600">
                    <i className="ri-check-line"></i>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}